import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import MomentCard from "../MomentCard";
import CommentModal from "../CommentModal";
import { colors, fontFamily } from "../../constants/theme";

const PAGE_SIZE = 5;

const SharedBitesComponent = ({ selectedRestaurant, userDoc, forceExpanded = false, navigation }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedMoment, setSelectedMoment] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [items, setItems] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const shouldShowContent = forceExpanded || isExpanded;

  const page = useQuery(
    userDoc?._id && selectedRestaurant?._id
      ? api.moments.getFriendsMomentsByRestaurant
      : "skip",
    userDoc?._id && selectedRestaurant?._id
      ? {
          userId: userDoc._id,
          restaurantId: selectedRestaurant._id,
          cursor: cursor ?? undefined,
          limit: PAGE_SIZE,
        }
      : "skip"
  );

  const totalCount = useQuery(
    userDoc?._id && selectedRestaurant?._id
      ? api.moments.getFriendsMomentsCountByRestaurant
      : "skip",
    userDoc?._id && selectedRestaurant?._id
      ? { userId: userDoc._id, restaurantId: selectedRestaurant._id }
      : "skip"
  );

  useEffect(() => {
    setCursor(null);
    setItems([]);
    setIsLoadingMore(false);
  }, [selectedRestaurant?._id, userDoc?._id]);

  useEffect(() => {
    if (page === undefined) return;
    setIsLoadingMore(false);
    if (cursor === null) {
      setItems(page.items ?? []);
    } else {
      setItems((prev) => {
        const seen = new Set(prev.map((m) => m._id));
        const next = (page.items ?? []).filter((m) => !seen.has(m._id));
        return [...prev, ...next];
      });
    }
  }, [page, cursor]);

  const loadMore = useCallback(() => {
    if (!page?.nextCursor || isLoadingMore) return;
    setIsLoadingMore(true);
    setCursor(page.nextCursor);
  }, [page, isLoadingMore]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleCommentPress = (moment) => {
    setSelectedMoment(moment);
    setShowCommentModal(true);
  };

  const handleCommentModalClose = () => {
    setShowCommentModal(false);
    setSelectedMoment(null);
  };

  const handleCommentAdded = () => {};

  const handleEditPress = useCallback(
    (moment) => {
      if (!moment?._id || !navigation?.navigate) return;
      navigation.navigate("CheckIn", { momentId: moment._id, editMoment: true });
    },
    [navigation]
  );

  const loadingFirstPage = page === undefined && cursor === null;
  /** Convex may keep previous page while refetching; explicit flag covers slow network */
  const loadingMorePending =
    isLoadingMore || (cursor !== null && page === undefined);
  const hasMore = page?.nextCursor != null;
  const showLoadMore = hasMore && items.length > 0;
  const headerCountText =
    totalCount !== undefined ? `(${totalCount})` : null;

  return (
    <View style={styles.root}>
      {forceExpanded ? (
        <View style={styles.inlineSectionHeader}>
          <Text style={styles.headerTitle}>Shared Bites</Text>
          {headerCountText != null ? (
            <Text style={styles.headerCount}>{headerCountText}</Text>
          ) : null}
        </View>
      ) : (
        <TouchableOpacity onPress={toggleExpanded} style={styles.headerRow}>
          <View style={styles.headerLeft}>
            <Text style={styles.headerTitle}>Shared Bites</Text>
            {headerCountText != null ? (
              <Text style={styles.headerCount}>{headerCountText}</Text>
            ) : null}
            <MaterialIcons
              name={isExpanded ? "keyboard-arrow-down" : "keyboard-arrow-right"}
              size={24}
              color={colors.brownMuted}
              style={{ marginLeft: 8 }}
            />
          </View>
        </TouchableOpacity>
      )}

      {shouldShowContent && (
        <View
          style={
            forceExpanded ? styles.contentBgPlain : styles.contentBgBordered
          }
        >
          {!forceExpanded && (
            <View style={{ height: 1, backgroundColor: "rgba(156, 107, 66, 0.5)" }} />
          )}

          {loadingFirstPage ? (
            <View style={styles.loadingRow}>
              <ActivityIndicator size="small" color={colors.disgoBrown} />
            </View>
          ) : items.length > 0 ? (
            <View>
              {items.map((moment) => (
                <MomentCard
                  key={moment._id}
                  moment={moment}
                  onCommentPress={() => handleCommentPress(moment)}
                  onEditPress={handleEditPress}
                  privyDid={userDoc?.privyDid}
                  onMomentUpdated={() => {}}
                  onRestaurantPress={() => {}}
                  isLiked={moment.isLiked || false}
                />
              ))}
              {showLoadMore ? (
                <TouchableOpacity
                  style={styles.loadMoreBtn}
                  onPress={loadMore}
                  disabled={loadingMorePending}
                  activeOpacity={0.85}
                >
                  {loadingMorePending ? (
                    <View style={styles.loadMoreLoadingRow}>
                      <ActivityIndicator size="small" color={colors.disgoBrown} />
                      <Text style={[styles.loadMoreLoadingText, { marginLeft: 10 }]}>
                        Loading…
                      </Text>
                    </View>
                  ) : (
                    <Text style={styles.loadMoreText}>Load more</Text>
                  )}
                </TouchableOpacity>
              ) : null}
              {loadingMorePending && items.length > 0 ? (
                <View style={styles.ghostBlock} accessibilityLabel="Loading more moments">
                  <View style={styles.skeletonLine} />
                  <View style={[styles.skeletonLine, styles.skeletonLineShort]} />
                  <View style={styles.skeletonCard} />
                </View>
              ) : null}
            </View>
          ) : (
            <View style={styles.emptyState}>
              <Text style={styles.emptyTitle}>No Shared Bites Yet</Text>
              <Text style={styles.emptyBody}>
                You, your friends, and people you follow haven&apos;t shared any moments from this
                restaurant yet.
              </Text>
            </View>
          )}
        </View>
      )}

      <CommentModal
        visible={showCommentModal}
        onClose={handleCommentModalClose}
        moment={selectedMoment}
        privyDid={userDoc?.privyDid}
        onCommentAdded={handleCommentAdded}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  root: { marginTop: 8 },
  inlineSectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 4,
    paddingBottom: 8,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-between",
    padding: 12,
  },
  headerLeft: { flexDirection: "row", alignItems: "center" },
  headerTitle: {
    fontSize: 24,
    fontFamily: fontFamily.abhayaBold,
    marginLeft: 4,
    color: colors.black,
  },
  headerCount: {
    fontSize: 16,
    fontFamily: fontFamily.abhayaBold,
    color: colors.gray600,
    marginLeft: 8,
  },
  contentBgPlain: { backgroundColor: colors.white },
  contentBgBordered: {
    backgroundColor: colors.white,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    borderTopWidth: 0,
    borderLeftWidth: 2,
    borderRightWidth: 2,
    borderBottomWidth: 2,
    borderColor: colors.gray100,
  },
  loadingRow: { paddingVertical: 24, alignItems: "center" },
  loadMoreBtn: {
    paddingVertical: 14,
    alignItems: "center",
    justifyContent: "center",
  },
  loadMoreLoadingRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  loadMoreLoadingText: {
    fontSize: 15,
    fontFamily: fontFamily.abhaya,
    color: colors.gray600,
  },
  loadMoreText: {
    fontSize: 16,
    fontFamily: fontFamily.abhayaBold,
    color: colors.disgoBrown,
  },
  ghostBlock: {
    paddingHorizontal: 12,
    paddingBottom: 16,
    paddingTop: 4,
  },
  skeletonLine: {
    height: 10,
    borderRadius: 5,
    backgroundColor: "rgba(107, 76, 59, 0.12)",
    marginBottom: 8,
    width: "72%",
  },
  skeletonLineShort: {
    width: "44%",
    marginBottom: 12,
  },
  skeletonCard: {
    height: 100,
    borderRadius: 12,
    backgroundColor: "rgba(107, 76, 59, 0.08)",
  },
  emptyState: { padding: 24, alignItems: "center" },
  emptyTitle: {
    fontSize: 18,
    fontFamily: fontFamily.abhayaBold,
    color: colors.gray600,
    marginBottom: 8,
  },
  emptyBody: {
    fontSize: 16,
    fontFamily: fontFamily.abhaya,
    color: colors.gray500,
    textAlign: "center",
  },
});

export default SharedBitesComponent;
